import { createContext, useCallback, useContext, useEffect, useReducer, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { findIndex, isEqual, omit } from 'lodash';
import { createPortal } from 'react-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { useDebounced } from '../../../utilities/Hooks';
import useSideNavState from '../../../stores/SideNavStateStore';
import useIsPrinting from '../../../utilities/PrintUtilities';

const MenuContext = createContext();

export default function MenuContextProvider(props) {
    const { children } = props;
    const [menus, setMenus] = useState([]);
    const [containers, assignContainer] = useReducer(containerReducer, {});
    const isPrinting = useIsPrinting();
    const shouldFloat = useMediaQuery('(max-width:1400px');
    const [, /* Not used */ sideNavStateActions] = useSideNavState();

    useEffect(() => {
        sideNavStateActions.setFloating(shouldFloat);
    }, [shouldFloat, sideNavStateActions]);

    // Menus are assigned at render time, as the ordering of components is parent -> child. If we used
    // a useEffect, the ordering is opposite (child's useEffects first, then parent's useEffects). This
    // causes a number of issues because we'd need to keep track of when we first render and reverse the
    // array of items before appending them when we navigate and change menus. You can change a ref at render
    // time and then the debounced resetMenus() function below is called slightly afterwards to "reset" the
    // menus state which actually triggers a render of the sidenav to display the correct menu.
    const menusRef = useRef([]);

    const resetMenus = useDebounced(
        useCallback(() => {
            // note the .reverse() call here. Makes it easy to do array desconstruction versus looking up length every time.
            setMenus(menusRef.current.map((menu) => ({ ...menu })).reverse());
        }, [])
    );

    const addOrUpdateMenu = useCallback(
        (menu) => {
            const { current: menusArr } = menusRef;
            const { id } = menu;
            const idx = findIndex(menusArr, { id });

            if (idx === -1) {
                menusArr.push(menu);
                resetMenus();
            } else {
                const old = menusArr[idx];

                // If any of the properties have changed, make sure we update them
                if (!isEqual(omit(old, 'render'), omit(menu, 'render'))) {
                    menusArr[idx] = menu;
                    resetMenus();
                }
            }
        },
        [resetMenus]
    );

    const removeMenu = useCallback(
        (id) => {
            const idx = findIndex(menusRef.current, { id });

            if (idx !== -1) {
                menusRef.current.splice(idx, 1);
                resetMenus();
            }
        },
        [resetMenus]
    );

    const getContainer = useCallback((id) => containers[id], [containers]);
    const setContainer = useCallback((id, el) => assignContainer({ id, el }), []);

    return (
        <DndProvider backend={HTML5Backend}>
            <MenuContext.Provider
                value={{
                    addOrUpdateMenu,
                    removeMenu,
                    menus,
                    setContainer,
                    getContainer,
                    shouldFloat: isPrinting || shouldFloat,
                }}
            >
                {children}
            </MenuContext.Provider>
        </DndProvider>
    );
}

function useMenuContainer(menuId) {
    return useContext(MenuContext).getContainer(menuId);
}

export function useSetMenuContainer(id) {
    const { setContainer } = useContext(MenuContext);

    // return callback for ref prop to assign a ref when we get one
    return useCallback((el) => setContainer(id, el), [id, setContainer]);
}

export function useMenus() {
    return useContext(MenuContext).menus;
}

export function useShouldFloat() {
    return useContext(MenuContext).shouldFloat;
}

function useNavigationMenu(options) {
    const { addOrUpdateMenu, removeMenu } = useContext(MenuContext);
    const { id, title, lastUrl, showBack, showTitle, render } = options;

    // this must be called synchronously in render, not in a useEffect
    // as the ordering is always correct. useEffect will call children
    // first, which gives us the wrong order compared to when we navigate
    // to sub menus.
    addOrUpdateMenu({ id, title, lastUrl, showBack, showTitle, render });

    useEffect(() => () => removeMenu(id), [removeMenu, id]);
}

function containerReducer(containers, { id, el }) {
    if (!el && containers[id]) {
        return omit(containers, id);
    }

    if (el && containers[id] !== el) {
        // assign or update container element (hot-reloading does this)
        return {
            ...containers,

            [id]: el,
        };
    }

    return containers;
}

/**
 * This component adds a navigation menu to the menu stack. Menus are
 * nested and accessed in LIFO queue. When a NavigationMenu component is
 * unmounted, it is removed from the stack and the menu animates the next
 * menu in.
 *
 * @param {Object} props
 * @param {String} props.id       The ID of the menu. This is used for automated testing via the HTML "id"
 *                                attribute and is used to menu item comparison.
 * @param {String} props.title    The "title" for this menu. Titles are visible for sub menus
 * @param {String} [props.lastUrl]  Used in a direct child menu. When the "Go Back" button is clicked
 *                                it takes the user to the parent menu's `lastUrl` URL target.
 * @param {Function} props.render   Render prop. This function is expeceted to return a React component which
 *                                will render the menu
 * @param {Boolean} [props.showBack=true] Shows the "Go Back" button. Defaults to `true`.
 * @param {Boolean} [props.showTitle=true] Shows the title of the menu
 * @param {Any} [props.children] Children are passed through
 */
export function NavigationMenu(props) {
    const { children, id, title, lastUrl, showBack, showTitle, render } = props;
    useNavigationMenu({ id, title, lastUrl, showBack, showTitle, render });

    // grab the element we are going to portal the menu to. Note, this can be
    // undefined if the sidenav has not rendered yet. In printing mode, the
    // sidenav will never render.
    const menuEl = useMenuContainer(id);

    return (
        <>
            {menuEl && createPortal(render(), menuEl)}
            {children || null}
        </>
    );
}

NavigationMenu.propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    lastUrl: PropTypes.string,
    showBack: PropTypes.bool,
    showTitle: PropTypes.bool,
    render: PropTypes.func.isRequired,
};

NavigationMenu.defaultProps = {
    lastUrl: undefined,
    showBack: true,
    showTitle: true,
};
